import React, { useState } from 'react';
import { submitLead } from '../../services/api';
import { PHONE_NUMBER, WHATSAPP_NUMBER } from '../../utils/constants';
import { Send, MessageCircle, AlertCircle, CheckCircle2, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import './EnquiryForm.css';

const PHONE_REGEX = /^[0-9]{10}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = (form) => {
  const e = {};
  if (!form.name.trim())               e.name  = 'Your name is required';
  else if (form.name.trim().length < 2) e.name = 'Please enter a valid name';
  if (!form.phone.trim())              e.phone = 'Phone number is required';
  else if (!PHONE_REGEX.test(form.phone.replace(/[\s-]/g, '')))
                                       e.phone = 'Enter a valid 10-digit mobile number';
  if (form.email && !EMAIL_REGEX.test(form.email)) e.email = 'Enter a valid email address';
  return e;
};

const EnquiryForm = ({ propertyId, propertyTitle, variant = 'default' }) => {
  const [form, setForm] = useState({ name:'', phone:'', countryCode:'+91', email:'', message:'' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (field, val) => {
    setForm(p => ({...p,[field]:val}));
    if (errors[field]) setErrors(p=>({...p,[field]:''}));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    try {
      await submitLead({
        name: form.name.trim(),
        phone: form.countryCode + form.phone.replace(/[\s-]/g,''),
        email: form.email.trim() || null,
        message: form.message.trim() || null,
        propertyId: propertyId || null,
      });
      setSubmitted(true);
      setForm({ name:'', phone:'', countryCode:'+91', email:'', message:'' });
      toast.success('Enquiry submitted successfully!');
    } catch (err) {
      const msg = err.response?.data?.message || 'Submission failed. Please call us directly.';
      toast.error(msg);
    } finally { setLoading(false); }
  };

  if (submitted) return (
    <div className="enquiry-success">
      <div className="enquiry-success__icon"><CheckCircle2 size={32}/></div>
      <h4>Thank You!</h4>
      <p>We've received your enquiry. Our team will contact you within 24 hours.</p>
      <button className="link-btn" onClick={()=>setSubmitted(false)} style={{marginTop:'12px',fontSize:'13px'}}>Submit another enquiry</button>
    </div>
  );

  return (
    <div className={`enquiry-form enquiry-form--${variant}`}>
      {variant === 'default' && (
        <div className="enquiry-form__header">
          <span className="section-label">Get In Touch</span>
          <h3>{propertyTitle ? `Enquire about this property` : 'Schedule a Site Visit'}</h3>
          {propertyTitle && <p className="enq-property-name">{propertyTitle}</p>}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="enq-group">
          <input
            type="text" placeholder="Your Full Name *"
            value={form.name} onChange={e=>set('name',e.target.value)}
            className={`enq-input${errors.name?' error':''}`}
          />
          {errors.name && <span className="enq-error"><AlertCircle size={11}/> {errors.name}</span>}
        </div>

        <div className="enq-group">
          <div style={{display:'flex',gap:'6px'}}>
            <select
              value={form.countryCode}
              onChange={e=>set('countryCode',e.target.value)}
              style={{height:'44px',padding:'0 8px',border:'1.5px solid #e5e7eb',borderRadius:'6px',fontSize:'13px',background:'#f9fafb',flexShrink:0,width:'90px',outline:'none'}}
            >
              <option value="+91">🇮🇳 +91</option>
              <option value="+1">🇺🇸 +1</option>
              <option value="+44">🇬🇧 +44</option>
              <option value="+971">🇦🇪 +971</option>
              <option value="+65">🇸🇬 +65</option>
              <option value="+61">🇦🇺 +61</option>
              <option value="+49">🇩🇪 +49</option>
              <option value="+33">🇫🇷 +33</option>
            </select>
            <input
              type="tel" placeholder="10-digit mobile *"
              value={form.phone} onChange={e=>set('phone',e.target.value.replace(/[^0-9]/g,''))}
              className={`enq-input${errors.phone?' error':''}`}
              maxLength={10} style={{flex:1}}
            />
          </div>
          {errors.phone && <span className="enq-error"><AlertCircle size={11}/> {errors.phone}</span>}
        </div>

        <div className="enq-group">
          <input
            type="email" placeholder="Email Address (optional)"
            value={form.email} onChange={e=>set('email',e.target.value)}
            className={`enq-input${errors.email?' error':''}`}
          />
          {errors.email && <span className="enq-error"><AlertCircle size={11}/> {errors.email}</span>}
        </div>

        <div className="enq-group">
          <textarea
            rows={3} placeholder="Message or preferred site visit time (optional)"
            value={form.message} onChange={e=>set('message',e.target.value)}
            className="enq-input"
          />
        </div>

        <button type="submit" className="enq-submit" disabled={loading}>
          <Send size={14}/> {loading ? 'Sending...' : 'Send Enquiry'}
        </button>
      </form>

      <div className="enq-divider"><span>or contact directly</span></div>

      <div className="enq-direct">
        <a href={`tel:${PHONE_NUMBER}`} className="enq-call"><Phone size={14}/> {PHONE_NUMBER}</a>
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi, I'm interested in ${propertyTitle||'a property at SPS'}. Please share details.`)}`}
          className="enq-whatsapp" target="_blank" rel="noreferrer"
        >
          <MessageCircle size={14}/> WhatsApp Us
        </a>
      </div>

      <p className="enq-disclaimer">By submitting, you agree to be contacted by our team. We respect your privacy.</p>
    </div>
  );
};

export default EnquiryForm;
